import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Activity,
  Search,
  X,
  Layers,
  FolderKanban,
  Inbox,
  Calendar,
  Users,
  ChevronRight,
  GitBranch,
  Star,
  CheckCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  red:   { label: 'Red',   dot: '#D31010', pill: 'bg-[#FBEAEA] text-[#D31010] border border-[#FFD0D0]' },
  amber: { label: 'Amber', dot: '#F5C400', pill: 'bg-[#FFFBE6] text-[#8A6800] border border-[#F5C40040]' },
  green: { label: 'Green', dot: '#228052', pill: 'bg-[#F2FFF2] text-[#228052] border border-[#CBF7E2]' },
  '':    { label: 'Blank', dot: '#666666', pill: 'bg-[#F2F2F2] text-[#666666] border border-[#E6E6E6]' },
};

const ragOrder = ['red', 'amber', 'green', ''];

const PROJECT_STAGE_ORDER = [
  'New', 'Discovery', 'Funnel', 'Ready to Analyze', 'Analyzing',
  'Planning', 'Refining', 'Reviewing', 'Ready for Developmen',
  'Developing', 'Execution', 'QA', 'UAT', 'Ready for Deployment', 'Deploying',
];

// Display-name overrides for known Jira typos / truncations
const STAGE_DISPLAY_NAMES = {
  'Ready for Developmen': 'Ready for Development',
};

const STAGE_COLORS = [
  '#F7FFFB', '#F0FFF8', '#E6FFF1', '#DBFFEA', '#CCFFE3', '#BDFFDC',
  '#ADFFD4', '#99FFC7', '#80FFB9', '#66FFAB', '#4DFF9D', '#33FF8F',
  '#14E97D', '#00C26D',
];

const TEAM_DISPLAY_NAMES = {
  CHAN: 'Channels', PS: 'Data Engineering', NBI: 'Data Insights',
  MD: 'Finance Technology', FPA: 'Financial Planning and Analysis',
  IN: 'Infrastructure - CCS', IGGS: 'Infrastructure - GSD and Global Services',
  IM: 'Infrastructure - M365', NET: 'Infrastructure - Network',
  ISNOW: 'Infrastructure - Service Management', IS: 'Infrastructure - SSO/OKTA',
  NP: 'Integration Services', ICA: 'IT AI Practice', IT: 'IT Kanban',
  TD: 'Marketing Technology', OCO: 'Office of the CIO (OCIO)',
  HR: 'People and Places Technology', PROF: 'Professional Services',
  RCA: 'Renewals and Product and Pricing', HD: 'Sales Technology', ND: 'Service Technology',
  IEA: 'IT Enterprise Architecture', IDO: 'IT DevOps',
};

const ACTIVE_STAGES = new Set(['Discovery', 'Planning', 'Execution', 'On Hold']);

// Parse a semicolon-or-comma-separated contributing-team string into an array of trimmed names.
const parseTeamList = (raw) =>
  raw ? raw.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean) : [];

// Returns true if any parsed contributing-team name is in the given displayNames Set.
const matchesContributing = (item, displayNames) =>
  parseTeamList(item.contributing_team).some(t => displayNames.has(t));

const matchesTeamFilter = (item, codes, showContrib, displayNames) =>
  showContrib
    ? matchesContributing(item, displayNames)
    : codes.includes(item.team_name);

const toggleFilter = (prev, value, shiftKey) => {
  if (shiftKey) {
    return prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
  }
  return prev.length === 1 && prev[0] === value ? [] : [value];
};

const getToggleCls = (on) => on
  ? { track: 'bg-[#00C26D] border-[#00C26D]', thumb: 'translate-x-5', label: 'Showing: contributing projects' }
  : { track: 'bg-slate-200 dark:bg-slate-700 border-transparent', thumb: 'translate-x-0', label: 'Showing: owned projects' };

const compareItems = (a, b) => {
  const kA = lexoKey(a.jira_rank), kB = lexoKey(b.jira_rank);
  if (kA !== kB) return kA < kB ? -1 : 1;
  const tA = a.tier || 2, tB = b.tier || 2;
  if (tA !== tB) return tA - tB;
  return ragOrder.indexOf(a.status) - ragOrder.indexOf(b.status);
};

const applyGoLiveFilters = (items, q, tiers, teams) =>
  items
    .filter(i => tiers.length === 0 || tiers.includes(i.tier || 2))
    .filter(i => !q || i.name?.toLowerCase().includes(q))
    .filter(i => !teams.length || teams.includes(i.team_name));

const cell = (v) => v || '—';
const teamCell = (name) => TEAM_DISPLAY_NAMES[name] || name || '—';
const bizSponsor = (item) => item.business_sponsor || item.sponsor || '—';

const CANDY_KEY = 'cio-candy-v1';
const loadCandyIds = () => {
  try {
    const raw = localStorage.getItem(CANDY_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};
const toggleCandySet = (prev, id) => {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id); else next.add(id);
  try { localStorage.setItem(CANDY_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
  return next;
};

const tierConfig = {
  1: { label: 'T1', cls: '' },
  2: { label: 'T2', cls: '' },
  3: { label: 'T3', cls: '' },
  4: { label: 'T4', cls: '' },
};
const DEFAULT_TIER = tierConfig[2];

const lexoKey = (r) => r ? r.split('|').pop() : '￿';

function useColumnResize(initial) {
  const [colWidths, setColWidths] = useState(initial);
  const dragRef = useRef(null);

  useEffect(() => {
    const onMouseMove = (ev) => {
      if (!dragRef.current) return;
      const w = Math.max(50, dragRef.current.startW + ev.clientX - dragRef.current.startX);
      setColWidths(p => ({ ...p, [dragRef.current.col]: w }));
    };
    const onMouseUp = () => { dragRef.current = null; };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleResizeStart = (col, e) => {
    e.preventDefault();
    const startW = colWidths[col] || e.currentTarget.parentElement.offsetWidth;
    dragRef.current = { col, startX: e.clientX, startW };
  };

  const handleKeyResize = (col, e) => {
    if (e.key === 'ArrowLeft') setColWidths(p => ({ ...p, [col]: Math.max(50, (p[col] || 100) - 10) }));
    else if (e.key === 'ArrowRight') setColWidths(p => ({ ...p, [col]: (p[col] || 100) + 10 }));
  };

  return { colWidths, handleResizeStart, handleKeyResize };
}

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return iso; }
};

const parseGoLiveDate = (str) => {
  if (!str) return null;
  const d = new Date(str);
  if (!Number.isNaN(d.getTime())) return d;
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    let yr = Number.parseInt(m[3], 10);
    if (yr < 100) yr += 2000;
    const d2 = new Date(yr, Number.parseInt(m[1], 10) - 1, Number.parseInt(m[2], 10));
    if (!Number.isNaN(d2.getTime())) return d2;
  }
  return null;
};

const GoLiveCard = ({ item, onOpen, dateLabel = 'Go-Live' }) => {
  const sc = statusConfig[item.status] || statusConfig.green;
  const tier = tierConfig[item.tier || 2] || tierConfig[2];
  const isProgram = item.entity_type === 'program';
  return (
    <button type="button"
      className="w-full text-left p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 hover:bg-[#00C26D]/5 transition-colors"
      onClick={() => onOpen(item)}
    >
      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
        <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${isProgram ? 'bg-[#00C26D]/10 text-[#00995A] border-[#00C26D]/30' : 'bg-cyan-100 text-cyan-700 border-cyan-200'}`}>
          {isProgram ? 'Program' : 'Project'}
        </span>
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sc.dot }} title={sc.label} />
        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">{tier.label}</span>
      </div>
      <p className="text-sm font-semibold text-slate-900 dark:text-white leading-snug truncate">{item.name}</p>
      {!isProgram && item.team_name && (
        <p className="text-[10px] text-slate-400 truncate mt-0.5">{TEAM_DISPLAY_NAMES[item.team_name] || item.team_name}</p>
      )}
      <p className="text-xs font-bold text-[#00995A] mt-1.5">{dateLabel}: {formatDate(item.due_date)}</p>
    </button>
  );
};
GoLiveCard.propTypes = { item: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired, dateLabel: PropTypes.string };

const GoLiveBucket = ({ label, headerCls, items, onOpen, emptyMsg, dateLabel = 'Go-Live' }) => (
  <div className={`rounded-xl border overflow-hidden ${headerCls.border}`}>
    <div className={`px-4 py-2.5 flex items-center justify-between ${headerCls.bg}`}>
      <div className="flex items-center gap-2">
        <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{label}</span>
      </div>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${headerCls.badge}`}>{items.length}</span>
    </div>
    <div className="p-2 space-y-2 max-h-[560px] overflow-y-auto bg-slate-50 dark:bg-slate-900/30">
      {items.length === 0
        ? <p className="text-xs text-slate-400 text-center py-6">{emptyMsg}</p>
        : items.map(item => <GoLiveCard key={item.id} item={item} onOpen={onOpen} dateLabel={dateLabel} />)}
    </div>
  </div>
);
GoLiveBucket.propTypes = {
  label: PropTypes.string.isRequired, headerCls: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired, onOpen: PropTypes.func.isRequired,
  emptyMsg: PropTypes.string.isRequired, dateLabel: PropTypes.string,
};

const ResizeHandle = ({ col, onStart, onKey }) => (
  <button
    type="button"
    aria-label={`Resize ${col} column`}
    className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-[#00C26D]/50 focus:bg-[#00C26D]/50 select-none p-0 border-0 bg-transparent"
    onMouseDown={(e) => onStart(col, e)}
    onKeyDown={(e) => onKey(col, e)}
  />
);

ResizeHandle.propTypes = {
  col: PropTypes.string.isRequired,
  onStart: PropTypes.func.isRequired,
  onKey: PropTypes.func.isRequired,
};


const ACTIVE_BTN   = 'bg-[#00C26D] text-[#1A1A1A] border-transparent shadow-md shadow-[#00C26D]/20';
const INACTIVE_BTN = 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700';

const TierFilterBar = ({ viewTab, setViewTab, tierFilters, setTierFilters, workstreamFilter, setWorkstreamFilter, totalPrograms, totalProjects }) => (
  <div className="flex items-center justify-between flex-wrap gap-4">
    <div className="flex items-center gap-2">
      <button onClick={() => { setViewTab('programs'); setWorkstreamFilter(null); }}
        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewTab === 'programs' ? 'bg-[#00C26D] text-[#1A1A1A] shadow-lg shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
        data-testid="tab-programs"
      >Programs ({totalPrograms})</button>
      <button onClick={() => setViewTab('projects')}
        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewTab === 'projects' ? 'bg-[#00C26D] text-[#1A1A1A] shadow-lg shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
        data-testid="tab-projects"
      >Projects ({totalProjects})</button>
      <button onClick={() => setViewTab('golive')}
        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewTab === 'golive' ? 'bg-[#00C26D] text-[#1A1A1A] shadow-lg shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
        data-testid="tab-golive"
      >Go-Live Calendar</button>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Filter by Tier</span>
      <button onClick={() => setTierFilters([])}
        className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${tierFilters.length === 0 ? ACTIVE_BTN : INACTIVE_BTN}`}
        data-testid="filter-tier-all"
      >All</button>
      {[1, 2, 3, 4].map(t => {
        const tc = tierConfig[t];
        const isActive = tierFilters.includes(t);
        return (
          <button key={t}
            onClick={() => setTierFilters(tierFilters.includes(t) ? tierFilters.filter(v => v !== t) : [...tierFilters, t])}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${tc.cls} ${isActive ? 'ring-2 ring-current ring-offset-1 shadow-sm' : 'opacity-50 hover:opacity-90'}`}
            data-testid={`filter-tier-${t}`}
          >{tc.label}</button>
        );
      })}
      {viewTab === 'projects' && (
        <>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-2">Workstream</span>
          <button onClick={() => setWorkstreamFilter(null)} className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${workstreamFilter === null ? ACTIVE_BTN : INACTIVE_BTN}`} data-testid="filter-workstream-all">All</button>
          <button onClick={() => setWorkstreamFilter(true)} className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${workstreamFilter === true ? ACTIVE_BTN : INACTIVE_BTN}`} data-testid="filter-workstream-yes">Yes</button>
          <button onClick={() => setWorkstreamFilter(false)} className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${workstreamFilter === false ? ACTIVE_BTN : INACTIVE_BTN}`} data-testid="filter-workstream-no">No</button>
        </>
      )}
    </div>
  </div>
);
TierFilterBar.propTypes = {
  viewTab: PropTypes.string.isRequired, setViewTab: PropTypes.func.isRequired,
  tierFilters: PropTypes.array.isRequired, setTierFilters: PropTypes.func.isRequired,
  workstreamFilter: PropTypes.any, setWorkstreamFilter: PropTypes.func.isRequired,
  totalPrograms: PropTypes.number.isRequired, totalProjects: PropTypes.number.isRequired,
};

function usePulseFilters() {
  const [viewTab, setViewTab] = useState('programs');
  const [statusFilters, setStatusFilters] = useState([]);
  const [tierFilters, setTierFilters] = useState([]);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState([]);
  const [workstreamFilter, setWorkstreamFilter] = useState(null);
  const [showContributing, setShowContributing] = useState(false);
  useEffect(() => { setSelectedTeamFilter([]); setPhaseFilter([]); setShowContributing(false); }, [viewTab]);
  const handleStageClick = (stage, e) => setPhaseFilter(prev => toggleFilter(prev, stage, e.shiftKey));
  const handleBacklogClick = (e) => setPhaseFilter(prev => toggleFilter(prev, 'New', e.shiftKey));
  const handleRagClick = (key, e) => setStatusFilters(prev => toggleFilter(prev, key, e.shiftKey));
  const handleTeamClick = (name, e) => setSelectedTeamFilter(prev => toggleFilter(prev, name, e.shiftKey));
  return {
    viewTab, setViewTab, statusFilters, setStatusFilters, tierFilters, setTierFilters,
    selectedTeamFilter, setSelectedTeamFilter, phaseFilter, setPhaseFilter,
    workstreamFilter, setWorkstreamFilter,
    showContributing, setShowContributing,
    handleStageClick, handleBacklogClick, handleRagClick, handleTeamClick,
  };
}

const PortfolioPulsePage = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nestedItem, setNestedItem] = useState(null);
  const [showNestedModal, setShowNestedModal] = useState(false);
  const {
    viewTab, setViewTab, statusFilters, setStatusFilters, tierFilters, setTierFilters,
    selectedTeamFilter, setSelectedTeamFilter, phaseFilter, setPhaseFilter,
    workstreamFilter, setWorkstreamFilter,
    showContributing, setShowContributing,
    handleStageClick, handleBacklogClick, handleRagClick, handleTeamClick,
  } = usePulseFilters();
  const { colWidths, handleResizeStart, handleKeyResize } = useColumnResize({
    tier: 64, name: 240, status: 80,
    tpm: 150, productLead: 150, productOwner: 150, startDate: 110,
    itPm: 150, teams: 150, parent: 200,
    dueDate: 110, itPov: 160, stage: 120, aiSummary: 130, workstream: 100,
  });
  const [cioCandyIds, setCioCandyIds] = useState(loadCandyIds);
  const toggleCioCandy = (id) => setCioCandyIds(prev => toggleCandySet(prev, id));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pulseRes = await axios.get(`${API}/executive/portfolio-pulse`);
        setData(pulseRes.data);
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    };
    fetchData();
  }, []);

  const openModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const baseRows = useMemo(() => {
    if (!data) return [];
    let items;
    if (viewTab === 'programs') {
      items = data.programs || [];
    } else {
      items = (data.projects || []).filter(p => (p.project_type || 'project') === 'project');
    }
    items = [...items].sort(compareItems);
    if (statusFilters.length > 0) items = items.filter(i => statusFilters.includes(i.status));
    if (tierFilters.length > 0) items = items.filter(i => tierFilters.includes(i.tier || 0));
    if (viewTab !== 'programs' && selectedTeamFilter.length > 0) {
      const displayNames = new Set(selectedTeamFilter.map(c => TEAM_DISPLAY_NAMES[c] || c));
      items = items.filter(i => matchesTeamFilter(i, selectedTeamFilter, showContributing, displayNames));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.key?.toLowerCase().includes(q) ||
        i.owner?.toLowerCase().includes(q) ||
        i.project_manager?.toLowerCase().includes(q)
      );
    }
    if (viewTab === 'projects' && workstreamFilter !== null) {
      items = items.filter(i => workstreamFilter ? i.is_workstream === 'Yes' : i.is_workstream !== 'Yes');
    }
    return items;
  }, [data, viewTab, searchQuery, statusFilters, tierFilters, selectedTeamFilter, workstreamFilter, showContributing]);

  const rows = useMemo(() => {
    if (phaseFilter.length === 0) return baseRows;
    return baseRows.filter(i =>
      phaseFilter.includes(viewTab === 'programs' ? i.current_status_text : i.raw_status)
    );
  }, [baseRows, phaseFilter, viewTab]);

  const allTeamNames = useMemo(() => {
    if (!data || viewTab === 'programs') return [];
    const typeFilter = 'project';
    const names = new Set();
    (data.projects || [])
      .filter(p => (p.project_type || 'project') === typeFilter)
      .forEach(p => p.team_name && names.add(p.team_name));
    return [...names].sort((a, b) => a.localeCompare(b)); // NOSONAR
  }, [data, viewTab]);

  const { kpiStats, filteredStats, isAllActive } = useMemo(() => {
    const stageCounts = {};
    baseRows.forEach(item => {
      const stage = viewTab === 'programs' ? item.current_status_text : item.raw_status;
      if (stage) stageCounts[stage] = (stageCounts[stage] || 0) + 1;
    });
    const activeTotal = Object.entries(stageCounts)
      .filter(([stage]) => stage !== 'New')
      .reduce((sum, [, n]) => sum + n, 0);
    const allNonNew = Object.keys(stageCounts).filter(s => s !== 'New');
    const allActive = allNonNew.length > 0
      && phaseFilter.length === allNonNew.length
      && allNonNew.every(s => phaseFilter.includes(s));
    const nonNew = rows.filter(item => {
      const stage = viewTab === 'programs' ? item.current_status_text : item.raw_status;
      return stage !== 'New';
    });
    const ragCounts = { red: 0, amber: 0, green: 0 };
    nonNew.forEach(item => {
      const rag = (item.status || '').toLowerCase();
      if (rag === 'red' || rag === 'amber' || rag === 'green') ragCounts[rag]++;
    });
    const nonBlankTotal = ragCounts.red + ragCounts.amber + ragCounts.green;
    return {
      kpiStats: { stageCounts, activeTotal, allNonNew },
      filteredStats: { total: nonNew.length, ragCounts, nonBlankTotal },
      isAllActive: allActive,
    };
  }, [baseRows, rows, viewTab, phaseFilter]);

  const goLiveData = useMemo(() => {
    if (!data) return { b30: [], b60: [], b90: [], overdue: [], completed: [] };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d30past = new Date(today); d30past.setDate(d30past.getDate() - 30);
    const d30 = new Date(today); d30.setDate(d30.getDate() + 30);
    const d60 = new Date(today); d60.setDate(d60.getDate() + 60);
    const d90 = new Date(today); d90.setDate(d90.getDate() + 90);
    const q = searchQuery ? searchQuery.toLowerCase() : '';
    const rawAll = (data.projects || [])
      .filter(p => (p.project_type || 'project') === 'project')
      .map(p => ({ ...p, entity_type: 'project' }))
      .filter(i => { const d = parseGoLiveDate(i.due_date); return d !== null && (i._gld = d) !== null; });
    const all = applyGoLiveFilters(rawAll, q, tierFilters, selectedTeamFilter);
    const byDate = (a, b) => a._gld - b._gld;
    const completedAll = (data.completed_projects || [])
      .map(p => { const d = parseGoLiveDate(p.due_date); return d ? { ...p, _gld: d } : null; })
      .filter(Boolean)
      .filter(i => i._gld >= d30past && i._gld <= today);
    const completed = applyGoLiveFilters(completedAll, q, tierFilters, selectedTeamFilter)
      .sort((a, b) => b._gld - a._gld);
    return {
      overdue:   all.filter(i => i._gld < today).sort(byDate),
      b30:       all.filter(i => i._gld >= today && i._gld <= d30).sort(byDate),
      b60:       all.filter(i => i._gld > d30 && i._gld <= d60).sort(byDate),
      b90:       all.filter(i => i._gld > d60 && i._gld <= d90).sort(byDate),
      completed,
    };
  }, [data, tierFilters, searchQuery, selectedTeamFilter]);

  if (isLoading) {
    return (
      <div className="p-8 space-y-6 animate-pulse" data-testid="pulse-loading">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>)}
        </div>
        <div className="h-[400px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-slate-500">Failed to load portfolio data</div>;

  const { summary } = data;
  const toggleCls = getToggleCls(showContributing);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="portfolio-pulse">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#00C26D]/10 dark:bg-[#00C26D]/15 rounded-xl flex items-center justify-center">
          <Activity className="w-6 h-6 text-[#00995A] dark:text-[#00D97E]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="pulse-title">Portfolio Pulse Report</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Grid roll-up across all Programs and Projects</p>
        </div>
      </div>

      {/* Dynamic KPI Tiles — hidden on Go-Live tab */}
      {viewTab !== 'golive' && (viewTab === 'programs' ? (

        <div className="grid grid-cols-3 gap-4">

          {/* In Backlog */}
          <button type="button"
            onClick={handleBacklogClick}
            title="Click to select • Shift+Click to add/remove"
            className={`glass-card rounded-xl p-4 border text-left w-full transition-all hover:border-[#00C26D]/50 ${phaseFilter.includes('New') ? 'border-[#00C26D] ring-2 ring-[#00C26D]/20' : 'border-slate-200 dark:border-slate-800'}`}
            data-testid="kpi-backlog-programs"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Inbox className="w-4 h-4 text-[#00995A]" />
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">In Backlog</p>
              </div>
              {phaseFilter.includes('New') && (
                <span className="text-[10px] text-[#00995A] font-semibold">Filtered</span>
              )}
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white leading-tight mt-1 mb-1.5">
              {kpiStats.stageCounts['New'] || 0}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">Programs with status New</p>
          </button>

          {/* Active Programs */}
          <div className={`glass-card rounded-xl p-3 border transition-all ${isAllActive ? 'border-[#00C26D] ring-2 ring-[#00C26D]/20' : 'border-slate-200 dark:border-slate-800'}`} data-testid="kpi-by-phase">
            <button type="button" className="w-full text-left"
              onClick={() => setPhaseFilter(isAllActive ? [] : kpiStats.allNonNew)}
              title="Click to show all active programs">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-[#00995A]" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Active Programs</p>
                </div>
                {isAllActive && <span className="text-[10px] text-[#00995A] font-semibold">Filtered</span>}
                {phaseFilter.length > 0 && !isAllActive && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); setPhaseFilter([]); }}
                    className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 underline">
                    Clear
                  </button>
                )}
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white leading-tight mt-1 mb-1.5">{kpiStats.activeTotal}</p>
            </button>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(kpiStats.stageCounts)
                .filter(([stage]) => ACTIVE_STAGES.has(stage))
                .sort(([, a], [, b]) => b - a)
                .map(([stage, count], idx) => (
                  <button key={stage} type="button"
                    onClick={(e) => handleStageClick(stage, e)}
                    title={`${STAGE_DISPLAY_NAMES[stage] || stage} — Click to select • Shift+Click to add/remove`}
                    style={{ backgroundColor: STAGE_COLORS[idx % STAGE_COLORS.length] }}
                    className={`rounded-lg px-2 py-2 text-left w-full transition-all text-[#1A1A1A] ${phaseFilter.includes(stage) ? 'ring-2 ring-[#1A1A1A] ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                    data-testid={`phase-filter-${stage.toLowerCase().replaceAll(/\s+/g, '-')}`}
                  >
                    <p className="text-[9px] font-bold uppercase tracking-wide opacity-70 leading-none truncate">{STAGE_DISPLAY_NAMES[stage] || stage}</p>
                    <p className="text-xl font-bold mt-1 leading-none">{count}</p>
                  </button>
                ))}
            </div>
          </div>

          {/* Total Programs */}
          <div className="glass-card rounded-xl p-3 border border-slate-200 dark:border-slate-800" data-testid="kpi-total">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#00995A]" />
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">RAG Status</p>
              </div>
              {statusFilters.length > 0 && (
                <button type="button" onClick={() => setStatusFilters([])}
                  className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 underline">
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                ['red',   'Red',   'bg-[#FBEAEA] text-[#D31010] border border-[#FFD0D0]'],
                ['amber', 'Amber', 'bg-[#FFFBE6] text-[#8A6800] border border-[#F5C40040]'],
                ['green', 'Green', 'bg-[#F2FFF2] text-[#228052] border border-[#CBF7E2]'],
                ['',      'Blank', 'bg-[#F2F2F2] text-[#666666] border border-[#E6E6E6]'],
              ].map(([key, label, cls]) => {
                const displayCount = key === '' ? filteredStats.total - filteredStats.nonBlankTotal : (filteredStats.ragCounts[key] || 0);
                const pct = filteredStats.total > 0 ? Math.round((displayCount / filteredStats.total) * 100) : 0;
                return (
                  <button key={key || 'blank'} type="button"
                    onClick={(e) => handleRagClick(key, e)}
                    title="Click to select • Shift+Click to add/remove"
                    className={`rounded-lg px-2 py-2 text-center w-full transition-all ${cls} ${statusFilters.includes(key) ? 'ring-2 ring-current ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                    data-testid={`rag-tile-${label.toLowerCase()}`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <p className="text-2xl font-bold leading-none">{displayCount}</p>
                      <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: (statusConfig[key] || statusConfig['']).dot }} />
                      <p className="text-xs font-normal opacity-60">({pct}%)</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

      ) : (

        <div className="grid grid-cols-3 gap-4">

            {/* In Backlog */}
            <button type="button"
              onClick={handleBacklogClick}
              title="Click to select • Shift+Click to add/remove"
              className={`glass-card rounded-xl p-4 border text-left w-full transition-all hover:border-[#00C26D]/50 ${phaseFilter.includes('New') ? 'border-[#00C26D] ring-2 ring-[#00C26D]/20' : 'border-slate-200 dark:border-slate-800'}`}
              data-testid="kpi-backlog"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Inbox className="w-4 h-4 text-[#00995A]" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">In Backlog</p>
                </div>
                {phaseFilter.includes('New') && (
                  <span className="text-[10px] text-[#00995A] font-semibold">Filtered</span>
                )}
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white leading-tight mt-1 mb-1.5">
                {kpiStats.stageCounts['New'] || 0}
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500">Projects with status New</p>
            </button>

            {/* Active Projects */}
            <div className={`glass-card rounded-xl p-3 border transition-all ${isAllActive ? 'border-[#00C26D] ring-2 ring-[#00C26D]/20' : 'border-slate-200 dark:border-slate-800'}`} data-testid="kpi-by-phase">
              <button type="button" className="w-full text-left"
                onClick={() => setPhaseFilter(isAllActive ? [] : kpiStats.allNonNew)}
                title="Click to show all active projects">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-[#00995A]" />
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Active Projects</p>
                  </div>
                  {isAllActive && <span className="text-[10px] text-[#00995A] font-semibold">Filtered</span>}
                  {phaseFilter.length > 0 && !isAllActive && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPhaseFilter([]); }}
                      className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 underline">
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-3xl font-bold text-slate-900 dark:text-white leading-tight mt-1 mb-1.5">{kpiStats.activeTotal}</p>
              </button>
              <div className="grid grid-cols-4 gap-1.5">
                {Object.entries(kpiStats.stageCounts)
                  .filter(([stage]) => ACTIVE_STAGES.has(stage))
                  .sort(([a], [b]) => {
                    const norm = s => s.toLowerCase().replaceAll(/\s+/g, '');
                    const ai = PROJECT_STAGE_ORDER.findIndex(s => norm(s) === norm(a));
                    const bi = PROJECT_STAGE_ORDER.findIndex(s => norm(s) === norm(b));
                    if (ai === -1 && bi === -1) return a.localeCompare(b);
                    if (ai === -1) return 1;
                    if (bi === -1) return -1;
                    return ai - bi;
                  })
                  .map(([stage, count], idx) => (
                    <button key={stage} type="button"
                      onClick={(e) => handleStageClick(stage, e)}
                      title={`${STAGE_DISPLAY_NAMES[stage] || stage} — Click to select • Shift+Click to add/remove`}
                      style={{ backgroundColor: STAGE_COLORS[idx % STAGE_COLORS.length] }}
                      className={`rounded-lg px-2 py-2 text-left w-full transition-all text-[#1A1A1A] ${phaseFilter.includes(stage) ? 'ring-2 ring-[#1A1A1A] ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                      data-testid={`phase-filter-${stage.toLowerCase().replaceAll(/\s+/g, '-')}`}
                    >
                      <p className="text-[9px] font-bold uppercase tracking-wide opacity-70 leading-none truncate">{STAGE_DISPLAY_NAMES[stage] || stage}</p>
                      <p className="text-xl font-bold mt-1 leading-none">{count}</p>
                    </button>
                  ))}
              </div>
            </div>

            {/* Total Projects */}
            <div className="glass-card rounded-xl p-3 border border-slate-200 dark:border-slate-800" data-testid="kpi-total">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <FolderKanban className="w-4 h-4 text-[#00995A]" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">RAG Status</p>
                </div>
                {statusFilters.length > 0 && (
                  <button type="button" onClick={() => setStatusFilters([])}
                    className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 underline">
                    Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  ['red',   'Red',   'bg-[#FBEAEA] text-[#D31010] border border-[#FFD0D0]'],
                  ['amber', 'Amber', 'bg-[#FFFBE6] text-[#8A6800] border border-[#F5C40040]'],
                  ['green', 'Green', 'bg-[#F2FFF2] text-[#228052] border border-[#CBF7E2]'],
                  ['',      'Blank', 'bg-[#F2F2F2] text-[#666666] border border-[#E6E6E6]'],
                ].map(([key, label, cls]) => {
                  const displayCount = key === '' ? filteredStats.total - filteredStats.nonBlankTotal : (filteredStats.ragCounts[key] || 0);
                  const pct = filteredStats.total > 0 ? Math.round((displayCount / filteredStats.total) * 100) : 0;
                  return (
                    <button key={key || 'blank'} type="button"
                      onClick={(e) => handleRagClick(key, e)}
                      title="Click to select • Shift+Click to add/remove"
                      className={`rounded-lg px-2 py-2 text-center w-full transition-all ${cls} ${statusFilters.includes(key) ? 'ring-2 ring-current ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                      data-testid={`rag-tile-${label.toLowerCase()}`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <p className="text-2xl font-bold leading-none">{displayCount}</p>
                        <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: (statusConfig[key] || statusConfig['']).dot }} />
                        <p className="text-xs font-normal opacity-60">({pct}%)</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

        </div>

      ))}

      {/* Team Filter Pills — Projects + Go-Live */}
      {viewTab !== 'programs' && (
        <div className="space-y-2" data-testid="team-filter-pills">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Filter by Team</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedTeamFilter([])}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter.length === 0 ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
              data-testid="pill-team-all"
            >All Teams</button>
            {allTeamNames.map(name => (
              <button key={name}
                onClick={(e) => handleTeamClick(name, e)}
                title="Click to select • Shift+Click to add/remove"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter.includes(name) ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
                data-testid={`pill-team-${name.toLowerCase().replaceAll(/[\s/]+/g, '-')}`}
              >{TEAM_DISPLAY_NAMES[name] || name}</button>
            ))}
          </div>
          {selectedTeamFilter.length > 0 && (
            <button
              type="button"
              role="switch"
              aria-checked={showContributing}
              onClick={() => setShowContributing(v => !v)}
              className="flex items-center gap-2 mt-1 group w-fit"
              data-testid="toggle-contributing"
            >
              <span className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 transition-colors duration-200 ${toggleCls.track}`}>
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${toggleCls.thumb}`} />
              </span>
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
                {toggleCls.label}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Tabs + Tier Filter */}
      <TierFilterBar
        viewTab={viewTab} setViewTab={setViewTab}
        tierFilters={tierFilters} setTierFilters={setTierFilters}
        workstreamFilter={workstreamFilter} setWorkstreamFilter={setWorkstreamFilter}
        totalPrograms={summary.total_programs} totalProjects={summary.total_projects}
      />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder={`Search ${viewTab} by name, key, PM, or owner...`}
          className="input-glass w-full pl-10 pr-10" data-testid="pulse-search"
        />
        {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
      </div>

      {/* Go-Live Calendar view */}
      {viewTab === 'golive' && (
        <div className="space-y-4">
          {/* Completed last 30 days */}
          <details className="rounded-xl border border-slate-300 dark:border-slate-600 overflow-hidden">
            <summary className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800/60 text-sm font-bold text-slate-600 dark:text-slate-300 cursor-pointer flex items-center gap-2">
              <CheckCheck className="w-3.5 h-3.5" />
              Completed — Last 30 Days
              <span className="ml-auto bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                {goLiveData.completed.length}
              </span>
            </summary>
            <div className="p-3 space-y-2 bg-white dark:bg-slate-900/20">
              {goLiveData.completed.length === 0
                ? <p className="text-xs text-slate-400 italic text-center py-2">No projects completed in the last 30 days</p>
                : goLiveData.completed.map(item => <GoLiveCard key={item.id} item={item} onOpen={openModal} dateLabel="Went Live" />)}
            </div>
          </details>
          {/* Overdue */}
          {goLiveData.overdue.length > 0 && (
            <details className="rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
              <summary className="px-4 py-2.5 bg-red-50 dark:bg-red-900/20 text-sm font-bold text-red-700 dark:text-red-400 cursor-pointer flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                Overdue ({goLiveData.overdue.length} items)
              </summary>
              <div className="p-3 space-y-2 bg-white dark:bg-slate-900/20">
                {goLiveData.overdue.map(item => <GoLiveCard key={item.id} item={item} onOpen={openModal} />)}
              </div>
            </details>
          )}
          {/* Upcoming buckets */}
          <div className="grid grid-cols-3 gap-4">
            <GoLiveBucket
              label="Next 30 Days"
              headerCls={{ border: 'border-orange-200 dark:border-orange-700', bg: 'bg-orange-50 dark:bg-orange-900/20', badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400' }}
              items={goLiveData.b30} onOpen={openModal} emptyMsg="No go-lives in the next 30 days"
            />
            <GoLiveBucket
              label="31–60 Days"
              headerCls={{ border: 'border-blue-200 dark:border-blue-700', bg: 'bg-blue-50 dark:bg-blue-900/20', badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400' }}
              items={goLiveData.b60} onOpen={openModal} emptyMsg="No go-lives in 31–60 days"
            />
            <GoLiveBucket
              label="61–90 Days"
              headerCls={{ border: 'border-[#CBF7E2] dark:border-[#00995A]/40', bg: 'bg-[#F2FFF2] dark:bg-[#00995A]/10', badge: 'bg-[#CBF7E2] dark:bg-[#00995A]/20 text-[#228052] dark:text-[#00D97E]' }}
              items={goLiveData.b90} onOpen={openModal} emptyMsg="No go-lives in 61–90 days"
            />
          </div>
        </div>
      )}

      {/* Grid Table — hidden on Go-Live tab */}
      {viewTab !== 'golive' && <div className="glass-card rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="pulse-grid">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 sticky top-0">
              <tr className="text-left">
                {[
                  ['tier','Tier'], ['name','Name'], ['status','Status'],
                ].map(([col, label]) => (
                  <th key={col} style={{ width: colWidths[col] }} className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative${col === 'status' ? ' text-center' : ''}`}>
                    {label}
                    <ResizeHandle col={col} onStart={handleResizeStart} onKey={handleKeyResize} />
                  </th>
                ))}
                {viewTab === 'programs' ? (
                  <>
                    {[['tpm','TPM'],['productLead','Product Lead'],['productOwner','Product Owner'],['startDate','Start Date']].map(([col, label]) => (
                      <th key={col} style={{ width: colWidths[col] }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                        {label}
                        <ResizeHandle col={col} onStart={handleResizeStart} onKey={handleKeyResize} />
                      </th>
                    ))}
                  </>
                ) : (
                  <>
                    {[['itPm','IT TPM'],['teams','Team'],['productOwner','Product Owner'],['startDate','Start Date']].map(([col, label]) => (
                      <th key={col} style={{ width: colWidths[col] }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                        {label}
                        <ResizeHandle col={col} onStart={handleResizeStart} onKey={handleKeyResize} />
                      </th>
                    ))}
                  </>
                )}
                <th style={{ width: colWidths.dueDate }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                  Go-Live Date
                  <ResizeHandle col="dueDate" onStart={handleResizeStart} onKey={handleKeyResize} />
                </th>
                {viewTab === 'programs' && (
                  <th style={{ width: colWidths.stage }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                    Phase
                    <ResizeHandle col="stage" onStart={handleResizeStart} onKey={handleKeyResize} />
                  </th>
                )}
                <th style={{ width: colWidths.itPov }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                  Milestones / Risk
                  <ResizeHandle col="itPov" onStart={handleResizeStart} onKey={handleKeyResize} />
                </th>
                {viewTab !== 'programs' && (
                  <th style={{ width: colWidths.stage }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                    Phase
                    <ResizeHandle col="stage" onStart={handleResizeStart} onKey={handleKeyResize} />
                  </th>
                )}
                {viewTab !== 'programs' && (
                  <>
                    <th style={{ width: colWidths.workstream }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                      Workstream
                      <ResizeHandle col="workstream" onStart={handleResizeStart} onKey={handleKeyResize} />
                    </th>
                    <th style={{ width: colWidths.parent }} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden relative">
                      Parent
                      <ResizeHandle col="parent" onStart={handleResizeStart} onKey={handleKeyResize} />
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((item, idx) => {
                const sc = statusConfig[item.status] || statusConfig.green;
                const tier = tierConfig[item.tier || 2] || DEFAULT_TIER;
                const isCioCandy = cioCandyIds.has(item.id);
                return (
                  <tr key={item.id}
                    data-cio={isCioCandy}
                    style={isCioCandy ? { background: 'linear-gradient(to right, rgba(147,51,234,0.13), rgba(147,51,234,0.04) 55%, transparent)' } : undefined}
                    className={`hover:bg-[#00C26D]/5 dark:hover:bg-[#00C26D]/5 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-slate-50/30 dark:bg-slate-800/20'}`}
                    onClick={() => openModal(item)}
                    data-testid={`grid-row-${item.key}`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">{tier.label}</span>
                    </td>
                    <td className="px-4 py-2 font-semibold text-slate-900 dark:text-white">
                      <Star className="candy-star inline-block w-3 h-3 mr-1 fill-purple-500 text-purple-500" />
                      {item.name}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: sc.dot }} title={sc.label} />
                    </td>
                    {viewTab === 'programs' ? (
                      <>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{cell(item.project_manager)}</td>
                        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{cell(item.product_lead)}</td>
                        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{cell(item.owner)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{formatDate(item.start_date)}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{cell(item.project_manager)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300 truncate">{teamCell(item.team_name)}</td>
                        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{cell(item.owner)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{formatDate(item.planned_start)}</td>
                      </>
                    )}
                    <td className="px-4 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300 truncate">{formatDate(item.due_date)}</td>
                    {viewTab === 'programs' && (
                      <td className="px-4 py-2 overflow-hidden text-slate-700 dark:text-slate-300">
                        <span className="text-xs line-clamp-2">{cell(item.current_status_text)}</span>
                      </td>
                    )}
                    <td className="px-4 py-2 overflow-hidden text-slate-700 dark:text-slate-300">
                      <span className="text-xs line-clamp-6">{cell(item.it_pov)}</span>
                    </td>
                    {viewTab !== 'programs' && (
                      <>
                        <td className="px-4 py-2 overflow-hidden text-slate-700 dark:text-slate-300">
                          <span className="text-xs line-clamp-2">{cell(item.raw_status)}</span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {item.is_workstream === 'Yes'
                            ? <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#00C26D]/15 text-[#00995A] border border-[#00C26D]/30">Yes</span>
                            : <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600">No</span>}
                        </td>
                        <td className="px-4 py-2 overflow-hidden" title={item.parent_summary || ''}>
                          {item.parent_key && <div className="font-mono text-[10px] text-slate-400">{item.parent_key}</div>}
                          <div className="text-xs text-slate-700 dark:text-slate-300 truncate">{cell(item.parent_summary)}</div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400" data-testid="pulse-empty">
              No {viewTab} match your filters
            </div>
          )}
        </div>
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400" data-testid="grid-footer">
          Showing <span className="font-semibold text-slate-700 dark:text-slate-300">{rows.length}</span> {viewTab}
        </div>
      </div>}

      {/* Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedItem && (() => {
            const sc = statusConfig[selectedItem.status] || statusConfig.green;
            const tier = tierConfig[selectedItem.tier || 2] || DEFAULT_TIER;
            const isProgram = selectedItem.entity_type === 'program';
            let typeLabel = 'Project';
            if (isProgram) typeLabel = 'Program';
            else if (selectedItem.project_type === 'enhancement') typeLabel = 'Enhancement';
            const relatedProjects = isProgram
              ? (data.projects || []).filter(p => p.program_name === selectedItem.name)
              : [];
            const startDate = isProgram ? selectedItem.start_date : selectedItem.planned_start;
            const isCandy = cioCandyIds.has(selectedItem.id);
            const candyBtnCls = isCandy
              ? { btn: 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-600 dark:text-purple-300', icon: 'fill-purple-500 text-purple-500', text: 'CIO Candy ✓' }
              : { btn: 'bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 hover:border-purple-200 hover:text-purple-500', icon: 'text-slate-400', text: 'Mark as CIO Candy' };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="space-y-1.5 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white leading-snug">
                      {selectedItem.name}
                    </div>
                    {!isProgram && selectedItem.program_name && (
                      <div className="text-base text-slate-500 dark:text-slate-400">
                        Program: <span className="font-bold text-slate-800 dark:text-slate-100">{selectedItem.program_name}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 text-xs font-bold rounded border ${isProgram ? 'bg-[#00C26D]/10 text-[#00995A] border-[#00C26D]/30' : 'bg-cyan-100 text-cyan-700 border-cyan-200'}`}>{typeLabel}</span>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded ${sc.pill}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sc.dot }} />
                        {sc.label}
                      </span>
                      <span className={`px-3 py-1 text-xs font-bold rounded border ${tier.cls}`}>{tier.label}</span>
                      <span className="text-xs text-slate-400 font-mono">{selectedItem.key}</span>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                {isProgram && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-1">
                    <p>TPM: <span className="font-medium text-slate-700 dark:text-slate-300">{cell(selectedItem.project_manager)}</span></p>
                    <p>Business Sponsor: <span className="font-medium text-slate-700 dark:text-slate-300">{bizSponsor(selectedItem)}</span></p>
                  </div>
                )}
                {!isProgram && (
                  <button type="button" onClick={() => toggleCioCandy(selectedItem.id)}
                    className={`mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold border transition-all ${candyBtnCls.btn}`}>
                    <Star className={`w-4 h-4 ${candyBtnCls.icon}`} />
                    {candyBtnCls.text}
                  </button>
                )}

                {/* Date tiles */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Start Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{formatDate(startDate)}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Go-Live Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{formatDate(selectedItem.due_date)}</p>
                  </div>
                </div>

                {/* Milestones/Risk (IT POV) */}
                <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 min-h-[60px]">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">Milestones / Risk</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{cell(selectedItem.it_pov)}</p>
                </div>

                {/* Status */}
                {selectedItem.current_status_text && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">Status</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedItem.current_status_text}</p>
                  </div>
                )}

                {/* Projects in this Program (clickable → nested popup) */}
                {isProgram && relatedProjects.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-2 flex items-center gap-1.5">
                      <FolderKanban className="w-3.5 h-3.5" />Projects in this Program ({relatedProjects.length})
                    </h4>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {relatedProjects.map(proj => {
                        const psc = statusConfig[proj.status] || statusConfig.green;
                        const ptier = tierConfig[proj.tier || 2] || DEFAULT_TIER;
                        return (
                          <button key={proj.id} type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700 hover:bg-[#00C26D]/5 dark:hover:bg-[#00C26D]/5 transition-colors text-left"
                            onClick={() => { setNestedItem(proj); setShowNestedModal(true); }}
                          >
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded shrink-0 ${psc.pill}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: psc.dot }} />
                              {psc.label}
                            </span>
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded border shrink-0 ${ptier.cls}`}>{ptier.label}</span>
                            <span className="font-mono text-xs text-slate-400 shrink-0">{proj.key}</span>
                            <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{proj.name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 ml-auto shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Parent (projects only) */}
                {!isProgram && (selectedItem.parent_key || selectedItem.parent_summary) && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">Parent</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {selectedItem.parent_key && <span className="font-mono text-xs text-slate-400 mr-2">{selectedItem.parent_key}</span>}
                      {cell(selectedItem.parent_summary)}
                    </p>
                  </div>
                )}

                {/* Contributing Teams */}
                {!isProgram && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />Contributing Teams
                    </h4>
                    {selectedItem.contributing_team ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedItem.contributing_team.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean).map(team => (
                          <span key={team} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00C26D]/10 dark:bg-[#00C26D]/15 text-[#00995A] dark:text-[#00D97E] border border-[#00C26D]/30 dark:border-[#00C26D]/30">
                            {team}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No contributing teams listed</p>
                    )}
                  </div>
                )}

                {/* AI Summary — bottom */}
                {!isProgram && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">AI Summary</h4>
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">Coming Soon</p>
                  </div>
                )}
                {isProgram && (
                  <>
                    <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-2 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />Contributing Teams
                      </h4>
                      {selectedItem.rollup_team_contributing ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedItem.rollup_team_contributing.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean).map(team => (
                            <span key={team} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00C26D]/10 dark:bg-[#00C26D]/15 text-[#00995A] dark:text-[#00D97E] border border-[#00C26D]/30 dark:border-[#00C26D]/30">
                              {team}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No contributing teams listed</p>
                      )}
                    </div>
                    <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">AI Summary</h4>
                      <p className="text-sm text-slate-400 dark:text-slate-500 italic">Coming Soon</p>
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Nested Project Detail Modal (popup-within-popup) */}
      <Dialog open={showNestedModal} onOpenChange={setShowNestedModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {nestedItem && (() => {
            const nsc = statusConfig[nestedItem.status] || statusConfig.green;
            const ntier = tierConfig[nestedItem.tier || 2] || DEFAULT_TIER;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="space-y-1.5 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white leading-snug">
                      {nestedItem.name}
                    </div>
                    {nestedItem.program_name && (
                      <div className="text-base text-slate-500 dark:text-slate-400">
                        Program: <span className="font-bold text-slate-800 dark:text-slate-100">{nestedItem.program_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-3 py-1 text-xs font-bold rounded border bg-cyan-100 text-cyan-700 border-cyan-200">Project</span>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded ${nsc.pill}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: nsc.dot }} />
                        {nsc.label}
                      </span>
                      <span className={`px-3 py-1 text-xs font-bold rounded border ${ntier.cls}`}>{ntier.label}</span>
                      <span className="text-xs text-slate-400 font-mono">{nestedItem.key}</span>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Start Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{formatDate(nestedItem.planned_start) || '—'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Go-Live Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{formatDate(nestedItem.due_date) || '—'}</p>
                  </div>
                </div>
                <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 min-h-[50px]">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">Milestones / Risk</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{nestedItem.it_pov || '—'}</p>
                </div>
                {nestedItem.current_status_text && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-1.5">Status</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{nestedItem.current_status_text}</p>
                  </div>
                )}
                {nestedItem.contributing_team && (
                  <div className="mt-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />Contributing Teams
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {nestedItem.contributing_team.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean).map(team => (
                        <span key={team} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00C26D]/10 dark:bg-[#00C26D]/15 text-[#00995A] dark:text-[#00D97E] border border-[#00C26D]/30 dark:border-[#00C26D]/30">
                          {team}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioPulsePage;
